import { ChangeEvent, ReactNode, useMemo, useState } from 'react';
import {
    Button,
    debounce,
    DebounceTimeoutValues,
    Select,
    Selected,
    SelectProps,
    StackItem,
    Typography,
    usePrevious,
} from '@optimus-web/core';
import { lt } from '@mews-framework/localization';

export interface AsyncSelectProps<T> extends Omit<SelectProps, 'children'> {
    name: string;
    useDataQuery: (searchValue: string) => {
        isFetching: boolean;
        data?: T;
        fetchNextPage?: () => void;
        hasNextPage?: boolean;
        isFetchingNextPage?: boolean;
    };
    renderData: (data: T) => ReactNode;
    defaultSearchValue?: string;
    defaultOptions?: ReactNode;
    initialEmptyMessage?: ReactNode;
}

export const AsyncSelect = <T,>({
    onChange,
    useDataQuery,
    renderData,
    defaultSearchValue = '',
    defaultOptions = null,
    emptyMessage,
    initialEmptyMessage,
    ...props
}: AsyncSelectProps<T>) => {
    const [searchValue, setSearchValue] = useState(defaultSearchValue);
    const [selected, setSelected] = useState<Selected | undefined>(props.selected);
    const prevSearchValue = usePrevious(searchValue);

    const { isFetching, data, fetchNextPage, hasNextPage, isFetchingNextPage } = useDataQuery(searchValue);

    const internalSelected = useMemo(
        () => (props.selected !== undefined ? props.selected : selected),
        [props.selected, selected]
    );

    const debouncedSetSearchTerm = useMemo(
        () =>
            debounce((newSearchValue: string) => {
                setSearchValue(newSearchValue);
            }, DebounceTimeoutValues.textInput),
        []
    );

    const handleSearchChange = (newSearchValue: string) => {
        // Immediate items cleanup if no search value
        if (!newSearchValue) {
            // Cancel any pending debounced search to avoid triggering a stale update when input is cleared
            debouncedSetSearchTerm.cancel();
            return setSearchValue(newSearchValue);
        }
        debouncedSetSearchTerm(newSearchValue);
    };

    const handleChange = (_event: ChangeEvent, selectedValue: Selected) => {
        setSelected(selectedValue);
        onChange?.(_event, selectedValue);
    };

    const asyncSelectDefaultChildren = searchValue.length > 0 && data ? renderData(data) : defaultOptions;

    const handleLoadMore = (event: React.MouseEvent) => {
        event.stopPropagation();

        if (fetchNextPage) {
            void fetchNextPage();
        }
    };

    const resolvedEmptyMessage =
        initialEmptyMessage && (!searchValue || (!prevSearchValue && isFetching)) ? initialEmptyMessage : emptyMessage;

    /**
     * In the Select component, chips are displayed based on the component's children.
     * To ensure a smooth search experience, the default options should always
     * be shown in the case of a multi-select (same as in SelectField).
     */
    return (
        <Select
            {...props}
            onSearchChange={handleSearchChange}
            onChange={handleChange}
            loader={isFetching}
            virtualized
            selected={internalSelected}
            emptyMessage={resolvedEmptyMessage}
        >
            {props.multiselect && defaultOptions}
            {props.multiselect && searchValue.length > 0 && data && renderData(data)}
            {props.multiselect ? null : asyncSelectDefaultChildren}
            {hasNextPage ? (
                <StackItem
                    padding="50"
                    key="load-more"
                    // @ts-expect-error: needed to prevent "Load more" item from being selected from "Select all" checkbox
                    disabled
                >
                    <Button variant="ghost" loading={isFetchingNextPage} onClick={handleLoadMore} wide type="button">
                        <Typography textStyle="bodyMediumStrong" as="div">
                            {lt('LoadMore')}
                        </Typography>
                    </Button>
                </StackItem>
            ) : null}
        </Select>
    );
};
